import {
  captureException as sentryCapture,
  addBreadcrumb as sentryBreadcrumb,
  setSentryUser,
  clearSentryUser,
  isSentryEnabled,
  Sentry,
} from '@/lib/sentry';
import type {
  BreadcrumbInput,
  CapturedEvent,
  ErrorContext,
  ErrorLevel,
  ErrorUser,
  IErrorTrackingProvider,
} from './types';

const MAX_EVENTS = 100;

export class SentryErrorTrackingProvider implements IErrorTrackingProvider {
  readonly name = 'sentry';
  private events: CapturedEvent[] = [];

  get isActive(): boolean {
    return isSentryEnabled();
  }

  captureException(error: unknown, context?: ErrorContext): string {
    const err = error instanceof Error ? error : new Error(String(error));
    sentryCapture(err, context);
    const id = Math.random().toString(36).slice(2, 10);
    this.push({
      id,
      message: err.message,
      level: context?.level ?? 'error',
      timestamp: Date.now(),
      tags: context?.tags,
      stack: err.stack,
    });
    return id;
  }

  captureMessage(message: string, level: ErrorLevel = 'info', context?: ErrorContext): string {
    if (this.isActive) {
      Sentry.withScope((scope) => {
        if (context?.tags) for (const [k, v] of Object.entries(context.tags)) scope.setTag(k, v);
        if (context?.extra) for (const [k, v] of Object.entries(context.extra)) scope.setExtra(k, v);
        scope.setLevel(level);
        Sentry.captureMessage(message);
      });
    }
    const id = Math.random().toString(36).slice(2, 10);
    this.push({ id, message, level, timestamp: Date.now(), tags: context?.tags });
    return id;
  }

  addBreadcrumb(crumb: BreadcrumbInput): void {
    sentryBreadcrumb({
      category: crumb.category,
      message: crumb.message,
      level: crumb.level === 'fatal' ? 'fatal' : crumb.level,
      data: crumb.data,
    });
  }

  setUser(user: ErrorUser | null): void {
    if (!user) {
      clearSentryUser();
      return;
    }
    setSentryUser(user.id);
  }

  getRecentEvents(limit = 25): CapturedEvent[] {
    return this.events.slice(0, limit);
  }

  private push(ev: CapturedEvent): void {
    this.events.unshift(ev);
    if (this.events.length > MAX_EVENTS) this.events.length = MAX_EVENTS;
  }
}
